/**
 * ProfileChip — the signed-in user's chip at the right end of every header.
 *
 * Collapsed it is only the avatar circle, so the bar reads as three icons
 * (notifications, settings, avatar). Hover — or keyboard focus, which never
 * fires :hover — slides out the name with the role under it. Clicking it opens
 * the user's own profile.
 *
 * One component for Admin / HR / Member: the three headers each had their own
 * copy of this markup and had already drifted apart (different fields shown,
 * one with a role badge, one without), so the chip lives here instead.
 *
 * Layout/animation lives in index.css under `.usr-info`.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import UserAvatar from "./UserAvatar";

// The chip only has room for a glance at the name.
const NAME_CHARS = 10;

const readUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("userdata") || "{}");
  } catch {
    return {};
  }
};

const initialsOf = (name) =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

export default function ProfileChip({ fullName, role }) {
  const navigate = useNavigate();

  const name = String(fullName || "").trim() || "User";
  const shortName =
    name.length > NAME_CHARS ? `${name.slice(0, NAME_CHARS)}…` : name;

  // Second line is the role, not the job title.
  const subLabel = String(role || "").trim();

  const openProfile = () => {
    const r = (localStorage.getItem("role") || "").toLowerCase().trim();
    const base = r === "admin" ? "/admin" : r === "hr" ? "/hr" : "/member";
    // member_id links to the employee record — some accounts have none, so fall
    // back to the settings profile page rather than a broken /teams/null.
    const memberId = readUserData().member_id;
    navigate(memberId ? `${base}/teams/${memberId}` : `${base}/settings/my-profile`);
    document.body.classList.remove("menu-active");
  };

  return (
    <div
      className="usr-info"
      role="button"
      tabIndex={0}
      title={`My profile — ${name}`}
      onClick={openProfile}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openProfile();
        }
      }}
    >
      <span className="usr-divider" aria-hidden="true" />
      <UserAvatar className="usr-face" initials={initialsOf(name)} refresh />
      {/* Truncated on purpose — the full name is in the chip's tooltip. */}
      <div className="usr-meta" aria-hidden="true">
        <b>{shortName}</b>
        <small>{subLabel}</small>
      </div>
    </div>
  );
}
